import { useMemo, useRef } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { docUrl } from "../../lib/docLinks";
import type { VariableDeleteConflictDetail } from "../../lib/errors";
import {
  Badge,
  Button,
  Drawer,
  EmptyState,
  HelpTip,
  Inline,
  InlineError,
  Page,
  PageHeader,
  Popover,
  Stack,
  Text,
} from "../../ui";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { VariableEditor, type VariableEditorProps } from "./VariableEditor";
import type { Variable } from "./variableFormState";

interface VariableListState {
  errorMessage?: string;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: Variable[];
  listStateKey: string;
}

type VariableReference = components["schemas"]["VariableReferenceResponse"];

interface VariableReferenceListState {
  errorMessage?: string;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetching?: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: VariableReference[];
}

export interface VariablesViewProps {
  deleteConflict: VariableDeleteConflictDetail | null;
  deleteError: string | null;
  deleteReferences: VariableReferenceListState;
  deleteVariableId: string | null;
  editor: VariableEditorProps | null;
  errorMessage?: string;
  isDeletePending: boolean;
  isPending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (variable: Variable) => void;
  onOpenCreate: () => void;
  onOpenEdit: (variableId: string) => void;
  onStartDelete: (variable: Variable) => void;
  scopeNames: ReadonlyMap<string, string>;
  variables: VariableListState;
}

function displayValue(variable: Variable) {
  const revision = variable.current_revision;
  const value = revision.default_value;
  const rendered =
    value === null
      ? "No default"
      : typeof value === "string"
        ? value
        : JSON.stringify(value);
  if (value === null) {
    return rendered;
  }
  return revision.is_sensitive ? (
    <Text as="span" variant="muted">
      Sensitive — hidden
    </Text>
  ) : (
    rendered
  );
}

interface VariableActionsProps {
  deleteConflict: VariableDeleteConflictDetail | null;
  deleteError: string | null;
  deleteReferences: VariableReferenceListState;
  isConfirming: boolean;
  isDeletePending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (variable: Variable) => void;
  onOpenEdit: (variableId: string) => void;
  onStartDelete: (variable: Variable) => void;
  variable: Variable;
}

function VariableActions({
  deleteConflict,
  deleteError,
  deleteReferences,
  isConfirming,
  isDeletePending,
  onCancelDelete,
  onConfirmDelete,
  onOpenEdit,
  onStartDelete,
  variable,
}: VariableActionsProps) {
  const deleteAnchorRef = useRef<HTMLButtonElement>(null);
  const isCheckingReferences =
    isConfirming &&
    (deleteReferences.isPending || deleteReferences.isFetching === true);
  const hasBlockingReferences =
    deleteReferences.items.length > 0 ||
    (deleteConflict?.referenceCount ?? 0) > 0;
  return (
    <Inline>
      <Button
        type="button"
        variant="secondary"
        onClick={() => {
          onOpenEdit(variable.id);
        }}
      >
        Edit
      </Button>
      <div className="marker-popover-anchor">
        <Button
          ref={deleteAnchorRef}
          aria-expanded={isConfirming}
          aria-haspopup="dialog"
          type="button"
          variant="secondary"
          onClick={() => {
            if (isConfirming) {
              onCancelDelete();
            } else {
              onStartDelete(variable);
            }
          }}
        >
          Delete
        </Button>
        <Popover
          align="end"
          anchorRef={deleteAnchorRef}
          aria-label={`Delete variable ${variable.key}`}
          isOpen={isConfirming}
          onDismiss={onCancelDelete}
        >
          <Stack gap={2}>
            <Text>Delete variable {variable.key}?</Text>
            {isCheckingReferences ? (
              <Text variant="muted">Checking references…</Text>
            ) : null}
            {deleteReferences.items.map((reference) => (
              <Text
                key={`${reference.resource_type}:${reference.resource_id}`}
                variant="muted"
              >
                Used by{" "}
                {reference.resource_type === "marker" ? "Marker" : "Scenario"}:{" "}
                {reference.display_name ?? reference.resource_id}
              </Text>
            ))}
            {deleteReferences.hasNextPage ? (
              <Button
                disabled={deleteReferences.isFetchingNextPage}
                type="button"
                variant="secondary"
                onClick={deleteReferences.fetchNextPage}
              >
                {deleteReferences.isFetchingNextPage
                  ? "Loading references…"
                  : "Load more references"}
              </Button>
            ) : null}
            <Text variant="muted">
              Personas bind by key name and are never tracked here.
            </Text>
            {deleteReferences.errorMessage !== undefined ? (
              <InlineError>{deleteReferences.errorMessage}</InlineError>
            ) : null}
            {deleteError !== null ? (
              <InlineError>{deleteError}</InlineError>
            ) : null}
            {deleteConflict !== null ? (
              <Stack gap={1}>
                <Text variant="muted">
                  Delete blocked by {deleteConflict.referenceCount} references.
                </Text>
                {deleteConflict.examples.map((example) => (
                  <Text
                    key={`${example.resourceType}:${example.resourceId}`}
                    variant="muted"
                  >
                    Server example —{" "}
                    {example.resourceType === "marker" ? "Marker" : "Scenario"}:{" "}
                    {example.resourceId}
                  </Text>
                ))}
                {deleteConflict.notice !== null ? (
                  <Text variant="muted">{deleteConflict.notice}</Text>
                ) : null}
              </Stack>
            ) : null}
            <Inline>
              <Button
                disabled={isDeletePending}
                type="button"
                variant="secondary"
                onClick={onCancelDelete}
              >
                Cancel
              </Button>
              <Button
                disabled={
                  isDeletePending ||
                  isCheckingReferences ||
                  hasBlockingReferences ||
                  deleteReferences.errorMessage !== undefined
                }
                type="button"
                variant="danger"
                onClick={() => {
                  onConfirmDelete(variable);
                }}
              >
                {isDeletePending ? "Deleting…" : "Delete variable"}
              </Button>
            </Inline>
          </Stack>
        </Popover>
      </div>
    </Inline>
  );
}

export function VariablesView({
  deleteConflict,
  deleteError,
  deleteReferences,
  deleteVariableId,
  editor,
  errorMessage,
  isDeletePending,
  isPending,
  onCancelDelete,
  onConfirmDelete,
  onOpenCreate,
  onOpenEdit,
  onStartDelete,
  scopeNames,
  variables,
}: VariablesViewProps) {
  const columns = useMemo<Array<ColumnDef<Variable>>>(
    () => [
      {
        accessorKey: "key",
        header: "Key",
        cell: ({ row }) => <Text as="span">{row.original.key}</Text>,
      },
      {
        header: "Lives on",
        id: "scope",
        cell: ({ row }) =>
          scopeNames.get(row.original.scope_id) ?? "Unavailable scope",
      },
      {
        header: "Type",
        id: "type",
        cell: ({ row }) => (
          <Badge>{row.original.current_revision.value_type}</Badge>
        ),
      },
      {
        header: "Default",
        id: "default",
        cell: ({ row }) => displayValue(row.original),
      },
      {
        header: "Required",
        id: "required",
        cell: ({ row }) =>
          row.original.current_revision.is_required ? "Required" : "Optional",
      },
      {
        accessorKey: "reference_count",
        header: "References",
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => {
          const variable = row.original;
          const isConfirming = deleteVariableId === variable.id;
          return (
            <VariableActions
              deleteConflict={isConfirming ? deleteConflict : null}
              deleteError={isConfirming ? deleteError : null}
              deleteReferences={
                isConfirming
                  ? deleteReferences
                  : {
                      ...deleteReferences,
                      errorMessage: undefined,
                      hasNextPage: false,
                      isPending: false,
                      items: [],
                    }
              }
              isConfirming={isConfirming}
              isDeletePending={isConfirming && isDeletePending}
              onCancelDelete={onCancelDelete}
              onConfirmDelete={onConfirmDelete}
              onOpenEdit={onOpenEdit}
              onStartDelete={onStartDelete}
              variable={variable}
            />
          );
        },
      },
    ],
    [
      deleteError,
      deleteConflict,
      deleteVariableId,
      deleteReferences,
      isDeletePending,
      onCancelDelete,
      onConfirmDelete,
      onOpenEdit,
      onStartDelete,
      scopeNames,
    ],
  );

  return (
    <Page variant="config">
      <PageHeader
        actions={
          <Button size="sm" onClick={onOpenCreate}>
            Declare variable
          </Button>
        }
        help={
          <HelpTip
            docHref={docUrl("concepts/variables")}
            docLabel="Variables"
            label="About variables"
          >
            {
              "Store typed constants with an Agent, Persona, or Scenario. Rows and run overrides can supply another value later."
            }
          </HelpTip>
        }
        title="Variables"
      />
      <Stack gap={6}>
        {errorMessage !== undefined ? (
          <InlineError>{errorMessage}</InlineError>
        ) : null}
        <PaginatedTableSection
          ariaLabel="Variables"
          columns={columns}
          emptyState={<EmptyState title="No variables" />}
          errorMessage={variables.errorMessage}
          getRowId={(variable) => variable.id}
          hasNextPage={variables.hasNextPage}
          isFetchingNextPage={variables.isFetchingNextPage}
          isPending={isPending || variables.isPending}
          items={variables.items}
          listStateKey={variables.listStateKey}
          loadMoreLabel="Load more variables"
          onLoadMore={variables.fetchNextPage}
          skeletonLabel="Loading variables"
          skeletonRows={6}
        />
      </Stack>
      <Drawer
        ariaLabel={
          editor?.variable === null
            ? "Declare variable"
            : `Edit variable ${editor?.variable?.key ?? ""}`
        }
        isOpen={editor !== null}
        onDismiss={() => {
          editor?.onCancel();
        }}
      >
        {editor !== null ? (
          <VariableEditor
            {...editor}
            key={
              editor.variable?.id ??
              `new:${editor.initialScopeType ?? "agent"}:${editor.initialScopeId ?? ""}:${editor.initialKey ?? ""}`
            }
          />
        ) : null}
      </Drawer>
    </Page>
  );
}
